<template>
  <div
    role="main"
    aria-label="Détails du fournisseur logistique"
  >
    <!-- Loading State -->
    <b-overlay
      :show="isLoading"
      spinner-variant="primary"
      rounded
    >
      <!-- Error Alert -->
      <b-alert
        v-if="error"
        variant="danger"
        show
      >
        {{ error }}
      </b-alert>

      <div v-if="supplier">
        <span
          id="supplier-details-desc"
          class="sr-only"
        >
          Page détaillée d’un fournisseur logistique. Consultez les informations professionnelles, coordonnées, tarification et statistiques. Utilisez les onglets pour naviguer entre les sections.
        </span>

        <!-- Supplier Header with Status and Actions -->
        <b-card
          class="mb-4"
          aria-label="En-tête fournisseur et actions"
        >
          <b-row class="align-items-center">
            <b-col
              cols="12"
              md="6"
              lg="7"
            >
              <!-- Supplier Identity -->
              <div class="d-flex align-items-center">
                <b-avatar
                  :text="getInitials(supplier.username)"
                  variant="light-primary"
                  size="lg"
                  class="mr-3"
                  aria-label="Initiales du fournisseur"
                />
                <div>
                  <h2
                    id="supplier-name"
                    class="mb-1"
                  >
                    {{ supplier.username }}
                  </h2>
                  <div
                    class="d-flex flex-wrap"
                    aria-label="Coordonnées du fournisseur"
                  >
                    <div class="mr-3 mb-1">
                      <feather-icon
                        icon="AtSignIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Adresse e-mail">{{ supplier.email }}</span>
                    </div>
                    <div class="mr-3 mb-1">
                      <feather-icon
                        icon="PhoneIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Téléphone personnel">{{ supplier.phone }}</span>
                    </div>
                    <div
                      v-if="supplier.phone2"
                      class="mr-3 mb-1"
                    >
                      <feather-icon
                        icon="PhoneCallIcon"
                        size="16"
                        class="text-primary mr-1"
                        aria-hidden="true"
                      />
                      <span aria-label="Téléphone société">{{ supplier.phone2 }}</span>
                    </div>
                  </div>
                  <div
                    class="mt-1"
                    aria-label="Statut et tarification du fournisseur"
                  >
                    <b-badge
                      :variant="supplier.is_active ? 'light-success' : 'light-danger'"
                      class="mr-1"
                      aria-label="Statut du compte"
                    >
                      {{ supplier.is_active ? 'Actif' : 'Inactif' }}
                    </b-badge>
                    <b-badge
                      v-if="supplier.weight_price"
                      variant="light-primary"
                      class="mr-1"
                      aria-label="Tarification par poids activée"
                    >
                      Prix par poids
                    </b-badge>
                    <b-badge
                      variant="light-info"
                      aria-label="Jours de paiement du fournisseur"
                    >
                      <feather-icon
                        icon="CalendarIcon"
                        size="12"
                        class="mr-25"
                      />
                      Paiement: {{ formattedPaymentDaysHeader }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="6"
              lg="5"
              class="d-flex justify-content-md-end mt-2 mt-md-0"
            >
              <!-- Action Buttons (Single Horizontal Row) -->
              <div class="d-flex align-items-center justify-content-md-end flex-wrap" style="gap: 0.5rem;">
                <b-button
                  v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
                  v-b-tooltip.hover
                  title="Modifier les informations"
                  variant="outline-primary"
                  size="md"
                  :to="{ name: 'supplier-edit', params: { id: supplier.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="15"
                    class="mr-50"
                  />
                  <span>Modifier</span>
                </b-button>

                <b-button
                  v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
                  v-b-tooltip.hover
                  title="Changer le mot de passe"
                  variant="outline-secondary"
                  size="md"
                  @click="openPasswordModal"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="15"
                    class="mr-50"
                  />
                  <span>Mot de passe</span>
                </b-button>

                <b-button
                  v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
                  variant="outline-warning"
                  size="md"
                  @click="toggleSupplierStatus"
                >
                  <feather-icon
                    icon="PowerIcon"
                    class="mr-50"
                    size="15"
                  />
                  <span>{{ supplier.is_active ? 'Désactiver le fournisseur' : 'Activer le fournisseur' }}</span>
                </b-button>

                <span
                  v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
                  v-b-tooltip.hover
                  :title="supplier.can_be_deleted !== false ? 'Supprimer' : (supplier.deletion_block_reason || 'Impossible de supprimer ce fournisseur')"
                  class="d-inline-block ml-50"
                >
                  <b-button
                    variant="outline-danger"
                    size="md"
                    :disabled="supplier.can_be_deleted === false"
                    :class="{ 'cursor-not-allowed not-allowed': supplier.can_be_deleted === false }"
                    @click="confirmSafeDelete"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      class="mr-50"
                      size="15"
                    />
                    <span>Supprimer</span>
                  </b-button>
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- KPI Header & Date Filter Toolbar -->
        <b-card no-body class="mb-4 shadow-sm">
          <b-card-body class="p-3">
            <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 1rem;">
              <div>
                <h5 class="mb-25 font-weight-bold text-dark d-flex align-items-center">
                  <feather-icon icon="BarChart2Icon" size="18" class="mr-50 text-primary" />
                  Statistiques & Indicateurs (KPIs)
                </h5>
                <small class="text-muted">Performances et activité calculées en temps réel</small>
              </div>

              <!-- Filter Presets Toolbar -->
              <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
                <b-form-radio-group
                  v-model="kpiPeriod"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  class="shadow-none"
                  @change="onKpiPeriodChange"
                >
                  <b-form-radio value="all">Tous</b-form-radio>
                  <b-form-radio value="today">Aujourd'hui</b-form-radio>
                  <b-form-radio value="7days">7 derniers jours</b-form-radio>
                  <b-form-radio value="this_month">Ce mois</b-form-radio>
                  <b-form-radio value="custom">Personnalisé</b-form-radio>
                </b-form-radio-group>
              </div>
            </div>

            <!-- Custom Date Range Pickers (visible when 'custom' selected) -->
            <b-row v-if="kpiPeriod === 'custom'" class="mt-3 pt-3 border-top">
              <b-col cols="12" sm="6" md="4" class="mb-2 mb-sm-0">
                <b-form-group label-class="form-label-premium" label="Date début" label-for="kpi-start-date" class="mb-0 font-weight-bold small">
                  <b-form-datepicker class="premium-input"
                    id="kpi-start-date"
                    v-model="kpiCustomStartDate"
                    size="sm"
                    placeholder="Choisir date début"
                    @input="fetchKpis"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" sm="6" md="4">
                <b-form-group label-class="form-label-premium" label="Date fin" label-for="kpi-end-date" class="mb-0 font-weight-bold small">
                  <b-form-datepicker class="premium-input"
                    id="kpi-end-date"
                    v-model="kpiCustomEndDate"
                    size="sm"
                    placeholder="Choisir date fin"
                    @input="fetchKpis"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- KPI Overview Cards Grid (6 Real Metrics) -->
        <b-overlay :show="isKpiLoading" rounded="sm" opacity="0.65" spinner-variant="primary">
          <b-row class="mb-4">
            <!-- 1. Total Commandes -->
            <b-col lg="2" md="4" sm="6" class="mb-3">
              <b-card no-body class="kpi-card border-left-primary shadow-sm h-100">
                <b-card-body class="py-2 px-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">Total Commandes</span>
                    <div class="avatar bg-light-primary p-25">
                      <feather-icon icon="PackageIcon" class="text-primary" size="18" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-dark">{{ supplier.total_orders_count || 0 }}</h4>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- 2. Commandes En Cours -->
            <b-col lg="2" md="4" sm="6" class="mb-3">
              <b-card no-body class="kpi-card border-left-warning shadow-sm h-100">
                <b-card-body class="py-2 px-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">En cours</span>
                    <div class="avatar bg-light-warning p-25">
                      <feather-icon icon="TruckIcon" class="text-warning" size="18" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-warning">{{ supplier.active_orders_count || 0 }}</h4>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- 3. Commandes Livrées -->
            <b-col lg="2" md="4" sm="6" class="mb-3">
              <b-card no-body class="kpi-card border-left-success shadow-sm h-100">
                <b-card-body class="py-2 px-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">Livrées</span>
                    <div class="avatar bg-light-success p-25">
                      <feather-icon icon="CheckCircleIcon" class="text-success" size="18" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-success">{{ supplier.delivered_orders_count || 0 }}</h4>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- 4. Chiffre d'Affaires Livré -->
            <b-col lg="3" md="6" sm="6" class="mb-3">
              <b-card no-body class="kpi-card border-left-info shadow-sm h-100">
                <b-card-body class="py-2 px-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">CA Livré (COD)</span>
                    <div class="avatar bg-light-info p-25">
                      <feather-icon icon="TrendingUpIcon" class="text-info" size="18" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-dark">{{ formatPrice(supplier.total_revenue) }}</h4>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- 5. Taux de Livraison -->
            <b-col lg="1" md="3" sm="6" class="mb-3" style="min-width: 120px; flex-grow: 1;">
              <b-card no-body class="kpi-card border-left-success shadow-sm h-100">
                <b-card-body class="py-2 px-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">Taux Livraison</span>
                    <div class="avatar bg-light-success p-25">
                      <feather-icon icon="PercentIcon" class="text-success" size="16" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-success">{{ supplier.delivery_success_rate || 0 }}%</h4>
                </b-card-body>
              </b-card>
            </b-col>

            <!-- 6. Taux de Retour -->
            <b-col lg="1" md="3" sm="6" class="mb-3" style="min-width: 120px; flex-grow: 1;">
              <b-card no-body class="kpi-card border-left-danger shadow-sm h-100">
                <b-card-body class="py-2 px-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small font-weight-bold">Taux Retour</span>
                    <div class="avatar bg-light-danger p-25">
                      <feather-icon icon="CornerUpLeftIcon" class="text-danger" size="16" />
                    </div>
                  </div>
                  <h4 class="mb-0 font-weight-bolder text-danger">{{ supplier.return_rate || 0 }}%</h4>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-overlay>

        <!-- Main Content Tabs -->
        <b-card no-body>
          <b-tabs
            v-model="activeTab"
            @input="onTabChange"
            pills
            card
            aria-label="Navigation des sections du fournisseur"
            aria-describedby="supplier-details-desc"
          >
            <!-- General Information Tab -->
            <b-tab
              title="Informations Générales"
              :active="activeTab === 0"
              aria-label="Informations générales du fournisseur"
            >
              <b-row>
                <!-- Contact Information -->
                <b-col lg="6">
                  <b-card
                    title="Informations de contact"
                    class="mb-4"
                    aria-label="Informations de contact du fournisseur"
                  >
                    <b-list-group flush>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Email</strong>
                        <span>{{ supplier.email }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Téléphone personnel</strong>
                        <span>{{ supplier.phone || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Téléphone société</strong>
                        <span>{{ supplier.phone2 || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Nom de la page</strong>
                        <span>{{ supplier.page_name || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Site web</strong>
                        <span>
                          <a
                            v-if="supplier.website_url"
                            :href="supplier.website_url"
                            target="_blank"
                            rel="noopener"
                          >{{ supplier.website_url }}</a>
                          <span v-else>Non spécifié</span>
                        </span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Adresse</strong>
                        <span>{{ supplier.address || 'Non spécifié' }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-card>
                </b-col>

                <!-- Business Information -->
                <b-col lg="6">
                  <b-card
                    title="Informations commerciales"
                    class="mb-4"
                    aria-label="Informations commerciales du fournisseur"
                  >
                    <b-list-group flush>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Matricule Fiscal</strong>
                        <span>{{ supplier.tax_identification_number || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between align-items-center">
                        <strong>Carte d'identité (CIN)</strong>
                        <span>{{ supplier.cin || 'Non spécifié' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between align-items-center">
                        <strong>Société de livraison (Sous-traitance)</strong>
                        <b-badge :variant="supplier.is_an_shipment_company ? 'light-success' : 'light-secondary'">
                          {{ supplier.is_an_shipment_company ? 'Oui (Sous-traitance)' : 'Non' }}
                        </b-badge>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between align-items-center">
                        <strong>Statut du compte</strong>
                        <b-badge :variant="supplier.is_active ? 'light-success' : 'light-danger'">
                          {{ supplier.is_active ? 'Actif' : 'Inactif' }}
                        </b-badge>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Dépôt</strong>
                        <span v-if="supplier.repository">{{ supplier.repository.name }}</span>
                        <span v-else>Non assigné</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Type de paiement</strong>
                        <span>{{ formatPaymentType(supplier.payment_type) }}</span>
                      </b-list-group-item>
                      <b-list-group-item
                        v-if="supplier.payment_type === 'virement'"
                        class="d-flex justify-content-between align-items-center"
                      >
                        <strong>RIB</strong>
                        <span :class="supplier.rib ? '' : 'text-muted'">{{ supplier.rib || 'Non renseigné' }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between align-items-center flex-wrap">
                        <strong>Jours de paiement</strong>
                        <div class="d-flex align-items-center flex-wrap justify-content-end">
                          <template v-if="supplier.invoice_generation_days && supplier.invoice_generation_days.length > 0">
                            <b-badge
                              v-for="day in supplier.invoice_generation_days"
                              :key="day"
                              variant="light-primary"
                              class="mr-50 mb-25"
                            >
                              {{ formatDayName(day) }}
                            </b-badge>
                          </template>
                          <span
                            v-else
                            class="text-muted mr-1 small"
                          >Non configurés</span>
                          <b-button
                            v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
                            variant="flat-primary"
                            size="sm"
                            class="p-0 ml-50 font-weight-bold"
                            @click="goToTab('invoice')"
                          >
                            <feather-icon
                              icon="CalendarIcon"
                              size="14"
                              class="mr-25"
                            />
                            Gérer
                          </b-button>
                        </div>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Prix de livraison</strong>
                        <span>{{ formatPrice(supplier.price_delivery) }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between">
                        <strong>Prix de retour</strong>
                        <span>{{ formatPrice(supplier.price_return) }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-card>
                </b-col>
              </b-row>

              <!-- Account Security & Audit Card -->
              <b-row>
                <b-col lg="12">
                  <b-card
                    title="Sécurité du compte & Audit"
                    class="mb-4 shadow-sm"
                    aria-label="Sécurité du compte et historique des modifications"
                  >
                    <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 1rem;">
                      <div
                        v-if="supplier.last_password_change"
                        class="d-flex align-items-center"
                      >
                        <div class="avatar bg-light-primary p-2 mr-2">
                          <feather-icon
                            icon="LockIcon"
                            size="20"
                            class="text-primary"
                          />
                        </div>
                        <div>
                          <strong class="d-block text-dark font-weight-bold">
                            Dernière modification du mot de passe
                          </strong>
                          <span class="small text-muted">
                            Modifié le {{ formatDateTime(supplier.last_password_change.created_at) }} par <strong>{{ supplier.last_password_change.changed_by_display }}</strong>
                          </span>
                        </div>
                      </div>
                      <div
                        v-else
                        class="text-muted small d-flex align-items-center"
                      >
                        <feather-icon
                          icon="ShieldIcon"
                          class="mr-50 text-secondary"
                          size="18"
                        />
                        <span>Aucune modification de mot de passe enregistrée pour ce compte.</span>
                      </div>

                      <b-button
                        v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
                        variant="outline-primary"
                        size="sm"
                        @click="openPasswordModal"
                      >
                        <feather-icon
                          icon="LockIcon"
                          size="14"
                          class="mr-50"
                        />
                        <span>Changer le mot de passe</span>
                      </b-button>
                    </div>

                    <!-- Collapsible Audit History List -->
                    <div
                      v-if="supplier.password_change_history && supplier.password_change_history.length > 0"
                      class="mt-3 pt-2 border-top"
                    >
                      <b-button
                        v-b-toggle.main-password-history-collapse
                        variant="flat-primary"
                        size="sm"
                        class="p-0 font-weight-bold"
                      >
                        <feather-icon
                          icon="HistoryIcon"
                          size="14"
                          class="mr-25"
                        />
                        Voir l'historique complet ({{ supplier.password_change_history.length }})
                      </b-button>

                      <b-collapse
                        id="main-password-history-collapse"
                        class="mt-2"
                      >
                        <b-list-group
                          flush
                          class="border rounded"
                        >
                          <b-list-group-item
                            v-for="log in supplier.password_change_history"
                            :key="log.id"
                            class="py-50 px-1 d-flex justify-content-between align-items-center small"
                          >
                            <span>
                              <feather-icon
                                icon="UserCheckIcon"
                                size="12"
                                class="mr-50 text-primary"
                              />
                              <strong>{{ log.changed_by_display }}</strong>
                            </span>
                            <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
                          </b-list-group-item>
                         </b-list-group>
                      </b-collapse>
                    </div>

                    <!-- Account audit metadata (create / update timestamps) -->
                    <div class="mt-3 pt-2 border-top">
                      <b-list-group flush class="mb-0">
                        <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                          <span>
                            <feather-icon
                              icon="PlusCircleIcon"
                              size="14"
                              class="mr-50 text-success"
                            />
                            <span class="text-muted">Compte créé le</span>
                          </span>
                          <span class="text-muted">{{ formatDateTime(supplier.created_at) }}</span>
                        </b-list-group-item>
                        <b-list-group-item class="py-50 px-0 d-flex justify-content-between align-items-center small">
                          <span>
                            <feather-icon
                              icon="RefreshCwIcon"
                              size="14"
                              class="mr-50 text-primary"
                            />
                            <span class="text-muted">Dernière mise à jour</span>
                          </span>
                          <span class="text-muted">{{ formatDateTime(supplier.updated_at) }}</span>
                        </b-list-group-item>
                      </b-list-group>
                    </div>
                  </b-card>
                </b-col>
              </b-row>

              <!-- Weight Pricing Section -->
              <b-card
                v-if="supplier.weight_price"
                title="Tarification par poids"
                class="mb-4"
                aria-label="Tableau de tarification par poids"
              >
                <div v-if="supplier.prices && supplier.prices.length > 0">
                  <div class="premium-table-wrapper">
                  <b-table
                    :items="supplier.prices"
                    striped
                    responsive
                    class="weight-pricing-table"
                    :fields="[
                      { key: 'weight.weight', label: 'Catégorie de poids' },
                      { key: 'weight.min', label: 'Poids min' },
                      { key: 'weight.max', label: 'Poids max' },
                      { key: 'weight.unit', label: 'Unité' },
                      { key: 'price_delivery', label: 'Prix livraison (TND)', thClass: 'price-column' },
                      { key: 'price_return', label: 'Prix retour (TND)', thClass: 'price-column' }
                    ]"
                    aria-label="Tableau des prix par catégorie de poids"
                    aria-describedby="weight-pricing-desc"
                  >
                    <template #table-caption>
                      <span
                        id="weight-pricing-desc"
                        class="sr-only"
                      >
                        Tableau listant les prix de livraison et de retour selon les catégories de poids définies pour ce fournisseur logistique.
                      </span>
                    </template>
                    <template #cell(weight.weight)="data">
                      <strong class="text-primary weight-category">{{ data.item.weight.weight }}</strong>
                    </template>
                    <template #cell(weight.min)="data">
                      <span v-if="data.item.weight.min">{{ data.item.weight.min }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(weight.max)="data">
                      <span v-if="data.item.weight.max">{{ data.item.weight.max }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(weight.unit)="data">
                      <b-badge variant="light-primary">
                        {{ data.item.weight.unit }}
                      </b-badge>
                    </template>
                    <template #cell(price_delivery)="data">
                      <strong class="text-success">{{ formatPrice(data.item.price_delivery) }}</strong>
                    </template>
                    <template #cell(price_return)="data">
                      <strong class="text-warning">{{ formatPrice(data.item.price_return) }}</strong>
                    </template>
                  </b-table>
                  </div>
                </div>
                <div
                  v-else-if="supplier.weights && supplier.weights.length > 0"
                  class="text-center py-3"
                >
                  <b-alert
                    show
                    variant="warning"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      class="mr-1"
                    />
                    Ce fournisseur utilise la tarification par poids mais aucun prix spécifique n'est configuré.
                  </b-alert>
                  <div class="premium-table-wrapper">
                  <b-table
                    :items="supplier.weights"
                    striped
                    responsive
                    :fields="[
                      { key: 'weight', label: 'Catégorie de poids' },
                      { key: 'min', label: 'Poids min' },
                      { key: 'max', label: 'Poids max' },
                      { key: 'unit', label: 'Unité' }
                    ]"
                  >
                    <template #cell(weight)="data">
                      <strong class="text-primary">{{ data.item.weight }}</strong>
                    </template>
                    <template #cell(min)="data">
                      <span v-if="data.item.min">{{ data.item.min }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(max)="data">
                      <span v-if="data.item.max">{{ data.item.max }}</span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                    </template>
                    <template #cell(unit)="data">
                      <b-badge variant="light-primary">
                        {{ data.item.unit }}
                      </b-badge>
                    </template>
                  </b-table>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center py-3"
                >
                  <b-alert
                    show
                    variant="info"
                  >
                    <feather-icon
                      icon="InfoIcon"
                      class="mr-1"
                    />
                    Aucune catégorie de poids configurée pour ce fournisseur.
                  </b-alert>
                </div>
              </b-card>
            </b-tab>

            <!-- Statistiques (Analytics Dashboard) Tab -->
            <b-tab title="Statistiques">
              <div class="supplier-analytics">
                <!-- Date Filter Bar inside Statistiques Tab -->
                <div class="analytics-toolbar d-flex justify-content-between align-items-center flex-wrap mb-4 p-3 rounded">
                  <div>
                    <h5 class="mb-25 font-weight-bold text-dark d-flex align-items-center">
                      <feather-icon icon="TrendingUpIcon" size="18" class="mr-50 text-primary" />
                      Tableau de Bord Analytique
                    </h5>
                    <small class="text-muted">Analyse détaillée des performances logistiques et financières</small>
                  </div>

                  <div class="d-flex align-items-center flex-wrap mr-25">
                    <b-form-radio-group
                      v-model="kpiPeriod"
                      buttons
                      button-variant="outline-primary"
                      size="sm"
                      class="shadow-none"
                      @change="onKpiPeriodChange"
                    >
                      <b-form-radio value="all">Tous</b-form-radio>
                      <b-form-radio value="today">Aujourd'hui</b-form-radio>
                      <b-form-radio value="7days">7 derniers jours</b-form-radio>
                      <b-form-radio value="this_month">Ce mois</b-form-radio>
                      <b-form-radio value="custom">Personnalisé</b-form-radio>
                    </b-form-radio-group>
                  </div>
                </div>

                <!-- Custom Date Pickers for Analytics (if custom period selected) -->
                <b-row v-if="kpiPeriod === 'custom'" class="mb-4">
                  <b-col cols="12" sm="6" md="4" class="mb-2 mb-sm-0">
                    <b-form-group label-class="form-label-premium" label="Date début" label-for="stats-start-date" class="mb-0 font-weight-bold small">
                      <b-form-datepicker class="premium-input"
                        id="stats-start-date"
                        v-model="kpiCustomStartDate"
                        size="sm"
                        placeholder="Choisir date début"
                        @input="fetchKpis"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4">
                    <b-form-group label-class="form-label-premium" label="Date fin" label-for="stats-end-date" class="mb-0 font-weight-bold small">
                      <b-form-datepicker class="premium-input"
                        id="stats-end-date"
                        v-model="kpiCustomEndDate"
                        size="sm"
                        placeholder="Choisir date fin"
                        @input="fetchKpis"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-overlay :show="isKpiLoading" rounded="sm" opacity="0.65" spinner-variant="primary">
                  <b-row>
                    <!-- Performance Overview Card -->
                    <b-col lg="6" class="mb-4">
                      <b-card no-body class="h-100 analytics-card">
                        <b-card-header class="analytics-card__header py-1">
                          <h6 class="mb-0 font-weight-bold d-flex align-items-center">
                            <feather-icon icon="TruckIcon" size="16" class="mr-50" />
                            Performance de Livraison
                          </h6>
                        </b-card-header>
                        <b-card-body class="py-3">
                          <b-row class="mb-3">
                            <b-col cols="6" class="mb-3">
                              <div class="stat-card p-3 bg-light-success rounded">
                                <small class="text-muted font-weight-bold d-block mb-1">Commandes Livrées</small>
                                <h3 class="mb-0 font-weight-bolder text-success">
                                  {{ supplier.delivered_orders_count || 0 }}
                                </h3>
                                <small class="text-success font-weight-bold">
                                  {{ supplier.delivery_success_rate || 0 }}% de réussite
                                </small>
                              </div>
                            </b-col>
                            <b-col cols="6" class="mb-3">
                              <div class="stat-card p-3 bg-light-danger rounded">
                                <small class="text-muted font-weight-bold d-block mb-1">Commandes Retournées</small>
                                <h3 class="mb-0 font-weight-bolder text-danger">
                                  {{ supplier.returned_orders_count || 0 }}
                                </h3>
                                <small class="text-danger font-weight-bold">
                                  {{ supplier.return_rate || 0 }}% de retour
                                </small>
                              </div>
                            </b-col>
                            <b-col cols="6">
                              <div class="stat-card p-3 bg-light-warning rounded">
                                <small class="text-muted font-weight-bold d-block mb-1">En Cours de Livraison</small>
                                <h3 class="mb-0 font-weight-bolder text-warning">
                                  {{ supplier.active_orders_count || 0 }}
                                </h3>
                                <small class="text-muted">En cours de traitement</small>
                              </div>
                            </b-col>
                            <b-col cols="6">
                              <div class="stat-card p-3 bg-light-info rounded">
                                <small class="text-muted font-weight-bold d-block mb-1">Total Commandes</small>
                                <h3 class="mb-0 font-weight-bolder text-info">
                                  {{ supplier.total_orders_count || 0 }}
                                </h3>
                                <small class="text-muted">Volume global</small>
                              </div>
                            </b-col>
                          </b-row>

                          <!-- Visual Ratio Progress Bar -->
                          <div class="mt-2">
                            <div class="d-flex justify-content-between align-items-center mb-50">
                              <small class="font-weight-bold text-dark">Ratio Livrées vs Retournées</small>
                              <small class="text-muted font-weight-bold">{{ supplier.delivery_success_rate || 0 }}% succès</small>
                            </div>
                            <b-progress height="10px" class="rounded">
                              <b-progress-bar :value="supplier.delivery_success_rate || 0" variant="success" />
                              <b-progress-bar :value="supplier.return_rate || 0" variant="danger" />
                            </b-progress>
                          </div>
                        </b-card-body>
                      </b-card>
                    </b-col>

                    <!-- Financial Overview Card -->
                    <b-col lg="6" class="mb-4">
                      <b-card no-body class="h-100 analytics-card">
                        <b-card-header class="analytics-card__header analytics-card__header--info py-1">
                          <h6 class="mb-0 font-weight-bold d-flex align-items-center">
                            <feather-icon icon="DollarSignIcon" size="16" class="mr-50" />
                            Analyse Financière COD
                          </h6>
                        </b-card-header>
                        <b-card-body class="py-3">
                          <b-list-group flush class="mb-3">
                            <!-- Chiffre d'affaires brut -->
                            <b-list-group-item class="d-flex justify-content-between align-items-center py-2">
                              <div>
                                <span class="font-weight-bold text-dark">
                                  <feather-icon icon="DollarSignIcon" size="16" class="text-success mr-50" />
                                  Chiffre d'Affaires Brut (COD)
                                </span>
                                <feather-icon
                                  v-b-tooltip.hover
                                  title="Montant total COD collecté sur les commandes livrées (statut Livré / Livré payé) dans la période."
                                  icon="HelpCircleIcon"
                                  size="14"
                                  class="text-muted ml-50 cursor-pointer"
                                />
                              </div>
                              <strong class="h5 mb-0 text-success">{{ formatPrice(supplier.total_revenue) }}</strong>
                            </b-list-group-item>

                            <!-- Total frais de livraison -->
                            <b-list-group-item class="d-flex justify-content-between align-items-center py-2">
                              <div>
                                <span class="font-weight-bold text-dark">
                                  <feather-icon icon="TruckIcon" size="16" class="text-primary mr-50" />
                                  Total Frais de Livraison
                                </span>
                                <feather-icon
                                  v-b-tooltip.hover
                                  title="Cumul des frais de livraison imputés sur l'ensemble des commandes pour la période."
                                  icon="HelpCircleIcon"
                                  size="14"
                                  class="text-muted ml-50 cursor-pointer"
                                />
                              </div>
                              <strong class="h5 mb-0 text-primary">{{ formatPrice(supplier.total_delivery_fees) }}</strong>
                            </b-list-group-item>

                            <!-- Montant Net à Reverser -->
                            <b-list-group-item
                              :class="((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) >= 0 ? 'analytics-net--positive' : 'analytics-net--negative'"
                              class="d-flex flex-column py-2 rounded mt-2"
                            >
                              <div class="d-flex justify-content-between align-items-center w-100">
                                <div>
                                  <span class="font-weight-bold text-dark">
                                    <feather-icon
                                      :icon="((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) >= 0 ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                                      size="16"
                                      :class="((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) >= 0 ? 'text-success mr-50' : 'text-danger mr-50'"
                                    />
                                    Montant Net à Reverser
                                  </span>
                                  <feather-icon
                                    v-b-tooltip.hover
                                    title="Calculé comme: (Chiffre d'Affaires COD - Total Frais de Livraison). Si les frais dépassent le COD collecté (ex. 0 commande livrée), le solde est débiteur."
                                    icon="HelpCircleIcon"
                                    size="14"
                                    class="text-muted ml-50 cursor-pointer"
                                  />
                                </div>
                                <strong
                                  :class="((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) >= 0 ? 'text-success' : 'text-danger'"
                                  class="h4 mb-0 font-weight-bolder"
                                >
                                  {{ formatPrice((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) }}
                                </strong>
                              </div>

                              <small
                                v-if="((supplier.total_revenue || 0) - (supplier.total_delivery_fees || 0)) < 0"
                                class="text-danger font-weight-bold mt-50"
                              >
                                <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25" />
                                Solde Débiteur: Les frais de livraison dépassent le COD collecté pour cette période.
                              </small>
                              <small
                                v-else
                                class="text-success font-weight-bold mt-50"
                              >
                                <feather-icon icon="CheckIcon" size="13" class="mr-25" />
                                Solde Créditeur: Montant net à verser au fournisseur.
                              </small>
                            </b-list-group-item>

                            <!-- Valeur moyenne par commande -->
                            <b-list-group-item class="d-flex justify-content-between align-items-center py-2 mt-1">
                              <div>
                                <span class="font-weight-bold text-dark">
                                  <feather-icon icon="ShoppingBagIcon" size="16" class="text-info mr-50" />
                                  Valeur Moyenne par Commande
                                </span>
                                <feather-icon
                                  v-b-tooltip.hover
                                  title="Panier moyen COD des commandes livrées (Price TTC moyen)."
                                  icon="HelpCircleIcon"
                                  size="14"
                                  class="text-muted ml-50 cursor-pointer"
                                />
                              </div>
                              <strong class="h5 mb-0 text-info">{{ formatPrice(supplier.average_order_value) }}</strong>
                            </b-list-group-item>
                          </b-list-group>
                        </b-card-body>
                      </b-card>
                    </b-col>
                  </b-row>
                </b-overlay>
              </div>
            </b-tab>

            <!-- Orders Tab (Embedded Interactive Orders Table) -->
            <b-tab title="Commandes">
              <div class="supplier-orders">
                <!-- Filter Bar -->
                <b-row class="mb-3 align-items-center">
                  <!-- Search Input -->
                  <b-col cols="12" md="4" class="mb-2 mb-md-0">
                    <b-input-group size="sm">
                      <b-form-input class="premium-input"
                        v-model="ordersSearch"
                        placeholder="Rechercher code, client, tél..."
                        @keyup.enter="fetchSupplierOrders"
                      />
                      <b-input-group-append>
                        <b-button variant="primary" size="sm" @click="fetchSupplierOrders">
                          <feather-icon icon="SearchIcon" size="14" />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-col>

                  <!-- Status Filter Dropdown -->
                  <b-col cols="12" sm="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                      v-model="ordersStatus"
                      size="sm"
                      :options="orderStatusOptions"
                      @change="fetchSupplierOrders"
                    />
                  </b-col>

                  <!-- Date Pickers & Reset -->
                  <b-col cols="12" sm="6" md="5" class="d-flex align-items-center orders-date-group">
                    <b-form-datepicker
                      v-model="ordersStartDate"
                      size="sm"
                      placeholder="Du..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierOrders"
                    />
                    <b-form-datepicker
                      v-model="ordersEndDate"
                      size="sm"
                      placeholder="Au..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierOrders"
                    />
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      class="btn-icon"
                      title="Réinitialiser les filtres"
                      @click="resetOrdersFilters"
                    >
                      <feather-icon icon="RotateCcwIcon" size="14" />
                    </b-button>
                  </b-col>
                </b-row>

                <!-- Premium Table Wrapper -->
                <div class="premium-table-wrapper">
                  <!-- Table Overlay Loading -->
                  <b-overlay :show="isOrdersLoading" rounded="sm" opacity="0.65" spinner-variant="primary">
                    <b-table
                      :items="supplierOrders"
                      :fields="orderTableFields"
                      striped
                      hover
                      responsive
                      show-empty
                      empty-text="Aucune commande trouvée pour ce fournisseur."
                      class="align-middle mb-0"
                    >
                      <!-- BL (Bon de Livraison) Column -->
                      <template #cell(tracking_number)="data">
                        <router-link
                          :to="{ name: 'order-show', params: { id: data.item.id } }"
                          class="font-weight-bold text-primary"
                        >
                          {{ data.item.tracking_number || `#${data.item.id}` }}
                        </router-link>
                        <small v-if="data.item.reference && data.item.reference !== data.item.tracking_number" class="text-muted d-block">
                          Réf: {{ data.item.reference }}
                        </small>
                      </template>

                      <!-- Recipient Info -->
                      <template #cell(client)="data">
                        <div class="font-weight-bold text-dark">{{ data.item.name || 'N/A' }}</div>
                        <small class="text-muted d-block">
                          <feather-icon icon="PhoneIcon" size="12" class="mr-25" />
                          {{ data.item.phone1 || 'Non renseigné' }}
                        </small>
                      </template>

                      <!-- Destination -->
                      <template #cell(destination)="data">
                        <span>{{ formatDestination(data.item) }}</span>
                      </template>

                      <!-- Status Badge -->
                      <template #cell(status)="data">
                        <b-badge :variant="getOrderStatusBadgeVariant(data.item.status)">
                          {{ getOrderStatusText(data.item.status) }}
                        </b-badge>
                      </template>

                      <!-- COD Amount -->
                      <template #cell(price_ttc)="data">
                        <strong class="text-success">{{ formatPrice(data.item.price_ttc) }}</strong>
                      </template>

                      <!-- Created At -->
                      <template #cell(created_at)="data">
                        <span class="small text-muted">{{ formatDateTime(data.item.created_at) }}</span>
                      </template>

                      <!-- Action Button -->
                      <template #cell(actions)="data">
                        <b-button
                          variant="flat-primary"
                          size="sm"
                          class="btn-icon"
                          v-b-tooltip.hover
                          title="Voir les détails de la commande"
                          :to="{ name: 'order-show', params: { id: data.item.id } }"
                        >
                          <feather-icon icon="EyeIcon" size="15" />
                        </b-button>
                      </template>
                    </b-table>

                    <!-- Pagination Bar -->
                    <div class="d-flex justify-content-between align-items-center flex-wrap p-2 orders-pagination">
                      <div class="d-flex align-items-center">
                        <small class="text-muted mr-2">Afficher</small>
                        <b-form-select
                          v-model="ordersPerPage"
                          size="sm"
                          :options="[10, 25, 50, 100]"
                          style="width: 75px;"
                          @change="fetchSupplierOrders"
                        />
                        <small class="text-muted ml-2">commandes sur <strong>{{ ordersTotalCount }}</strong></small>
                      </div>
                      <b-pagination
                        v-model="ordersPage"
                        :total-rows="ordersTotalCount"
                        :per-page="ordersPerPage"
                        size="sm"
                        class="mb-0"
                        @change="onOrdersPageChange"
                      />
                    </div>
                  </b-overlay>
                </div>
              </div>
            </b-tab>

            <!-- Factures Tab (Embedded Interactive Invoices Table) -->
            <!-- Visible to the same roles that can access Planification
                 (admin / superadmin / sales) so the billing team can review a
                 supplier's invoice history without leaving the detail page. -->
            <b-tab
              v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
              title="Factures"
            >
              <div class="supplier-invoices">
                <!-- Filter Bar -->
                <b-row class="mb-3 align-items-center">
                  <!-- Status Filter -->
                  <b-col cols="12" sm="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                      v-model="invoicesStatus"
                      size="sm"
                      :options="invoiceStatusOptions"
                      @change="fetchSupplierInvoices"
                    />
                  </b-col>

                  <!-- Date Pickers & Reset -->
                  <b-col cols="12" sm="6" md="9" class="d-flex align-items-center invoices-date-group">
                    <b-form-datepicker
                      v-model="invoicesStartDate"
                      size="sm"
                      placeholder="Du..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierInvoices"
                    />
                    <b-form-datepicker
                      v-model="invoicesEndDate"
                      size="sm"
                      placeholder="Au..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierInvoices"
                    />
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      class="btn-icon"
                      title="Réinitialiser les filtres"
                      @click="resetInvoicesFilters"
                    >
                      <feather-icon icon="RotateCcwIcon" size="14" />
                    </b-button>
                  </b-col>
                </b-row>

                <!-- Premium Table Wrapper -->
                <div class="premium-table-wrapper">
                  <b-overlay :show="isInvoicesLoading" rounded="sm" opacity="0.65" spinner-variant="primary">
                    <b-table
                      :items="supplierInvoices"
                      :fields="invoiceTableFields"
                      striped
                      hover
                      responsive
                      show-empty
                      empty-text="Aucune facture trouvée pour ce fournisseur."
                      class="align-middle mb-0"
                    >
                      <!-- Invoice number -->
                      <template #cell(id)="data">
                        <router-link
                          :to="{ name: 'invoice-show', params: { id: data.item.id } }"
                          class="font-weight-bold text-primary"
                        >
                          #{{ data.item.id }}
                        </router-link>
                      </template>

                      <!-- Status Badge -->
                      <template #cell(status)="data">
                        <b-badge :variant="getInvoiceStatusVariant(data.item.status)">
                          {{ formatInvoiceStatus(data.item.status) }}
                        </b-badge>
                      </template>

                      <!-- Payment validation pill -->
                      <template #cell(is_payment_validated)="data">
                        <b-badge
                          :variant="data.item.is_payment_validated ? 'light-success' : 'light-secondary'"
                          class="text-capitalize"
                        >
                          {{ data.item.is_payment_validated ? 'Validé' : 'En attente' }}
                        </b-badge>
                      </template>

                      <!-- Total TTC -->
                      <template #cell(total_ttc)="data">
                        <strong class="text-dark">{{ formatPrice(data.item.total_ttc) }}</strong>
                      </template>

                      <!-- Net à payer -->
                      <template #cell(net_a_payer)="data">
                        <strong class="text-success">{{ formatPrice(data.item.net_a_payer) }}</strong>
                      </template>

                      <!-- Confirmed At -->
                      <template #cell(confirmed_at)="data">
                        <span class="small text-muted">
                          {{ data.item.confirmed_at ? formatDateTime(data.item.confirmed_at) : '—' }}
                        </span>
                      </template>

                      <!-- Action Button -->
                      <template #cell(actions)="data">
                        <b-button
                          variant="flat-primary"
                          size="sm"
                          class="btn-icon"
                          v-b-tooltip.hover
                          title="Voir les détails de la facture"
                          :to="{ name: 'invoice-show', params: { id: data.item.id } }"
                        >
                          <feather-icon icon="EyeIcon" size="15" />
                        </b-button>
                      </template>
                    </b-table>

                    <!-- Pagination Bar -->
                    <div class="d-flex justify-content-between align-items-center flex-wrap p-2 invoices-pagination">
                      <div class="d-flex align-items-center">
                        <small class="text-muted mr-2">Afficher</small>
                        <b-form-select
                          v-model="invoicesPerPage"
                          size="sm"
                          :options="[10, 25, 50, 100]"
                          style="width: 75px;"
                          @change="fetchSupplierInvoices"
                        />
                        <small class="text-muted ml-2">factures sur <strong>{{ invoicesTotalCount }}</strong></small>
                      </div>
                      <b-pagination
                        v-model="invoicesPage"
                        :total-rows="invoicesTotalCount"
                        :per-page="invoicesPerPage"
                        size="sm"
                        class="mb-0"
                        @change="onInvoicesPageChange"
                      />
                    </div>
                  </b-overlay>

                  <!-- Empty state CTA -->
                  <div
                    v-if="!isInvoicesLoading && invoicesTotalCount === 0"
                    class="text-center py-3"
                  >
                    <feather-icon icon="FileTextIcon" size="32" class="text-muted mb-1" />
                    <p class="text-muted mb-2">Aucune facture n'a encore été générée pour ce fournisseur.</p>
                    <b-button
                      variant="primary"
                      size="sm"
                      :to="{ name: 'invoice-add', query: { supplier: supplier.id } }"
                    >
                      <feather-icon icon="PlusCircleIcon" size="14" class="mr-25" />
                      Créer une facture
                    </b-button>
                  </div>
                </div>
              </div>
            </b-tab>

            <!-- Bons de Retour Tab (Embedded Interactive Return Vouchers Table) -->
            <!-- Visible to admin / superadmin / sales / manager / supplier.
                 Reuses the supplier-scoped list endpoint
                 /api/return-vouchers/?supplier=<id> — the backend already
                 enforces supplier ownership via get_queryset (suppliers only
                 see their own vouchers), so this tab never widens access.
                 Suppliers additionally get the "Valider automatiquement"
                 action (validate-return-voucher-orders). -->
            <b-tab
              v-if="user && ['admin', 'superadmin', 'sales', 'manager', 'supplier'].includes(user.role)"
              title="Bons de retour"
            >
              <div class="supplier-return-vouchers">
                <!-- Filter Bar -->
                <b-row class="mb-3 align-items-center">
                  <!-- Status Filter -->
                  <b-col cols="12" sm="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                      v-model="returnVouchersStatus"
                      size="sm"
                      :options="returnVoucherStatusOptions"
                      @change="fetchSupplierReturnVouchers"
                    />
                  </b-col>

                  <!-- Date Pickers & Reset -->
                  <b-col cols="12" sm="6" md="9" class="d-flex align-items-center return-vouchers-date-group">
                    <b-form-datepicker
                      v-model="returnVouchersStartDate"
                      size="sm"
                      placeholder="Du..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierReturnVouchers"
                    />
                    <b-form-datepicker
                      v-model="returnVouchersEndDate"
                      size="sm"
                      placeholder="Au..."
                      class="premium-input flex-grow-1 mr-50"
                      @input="fetchSupplierReturnVouchers"
                    />
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      class="btn-icon"
                      title="Réinitialiser les filtres"
                      @click="resetReturnVouchersFilters"
                    >
                      <feather-icon icon="RotateCcwIcon" size="14" />
                    </b-button>
                  </b-col>
                </b-row>

                <!-- Premium Table Wrapper -->
                <div class="premium-table-wrapper">
                  <b-overlay :show="isReturnVouchersLoading" rounded="sm" opacity="0.65" spinner-variant="primary">
                    <b-table
                      :items="supplierReturnVouchers"
                      :fields="returnVoucherTableFields"
                      striped
                      hover
                      responsive
                      show-empty
                      empty-text="Aucun bon de retour trouvé pour ce fournisseur."
                      class="align-middle mb-0"
                    >
                      <!-- Voucher number -->
                      <template #cell(id)="data">
                        <router-link
                          :to="{ name: 'bon-retour-show', params: { id: data.item.id } }"
                          class="font-weight-bold text-primary"
                        >
                          #{{ data.item.id }}
                        </router-link>
                      </template>

                      <!-- Orders count -->
                      <template #cell(orders_count)="data">
                        <strong>{{ data.item.orders_count }}</strong>
                      </template>

                      <!-- Total price -->
                      <template #cell(total_price)="data">
                        <strong class="text-dark">{{ formatPrice(data.item.total_price) }}</strong>
                      </template>

                      <!-- Return price -->
                      <template #cell(total_return_price)="data">
                        <strong class="text-danger">{{ formatPrice(data.item.total_return_price) }}</strong>
                      </template>

                      <!-- Validation status badge (glassmorphic compact flag per design system §7) -->
                      <template #cell(is_validated)="data">
                        <span
                          class="return-voucher-status-flag"
                          :class="data.item.is_validated ? 'validated' : 'pending'"
                        >
                          <feather-icon
                            :icon="data.item.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                            size="12"
                            class="mr-25"
                          />
                          {{ data.item.is_validated ? 'Validé' : 'En attente' }}
                        </span>
                      </template>

                      <!-- Created At -->
                      <template #cell(created_at)="data">
                        <span class="small text-muted">
                          {{ data.item.created_at ? formatDateTime(data.item.created_at) : '—' }}
                        </span>
                      </template>

                      <!-- Actions -->
                      <template #cell(actions)="data">
                        <b-button
                          variant="flat-primary"
                          size="sm"
                          class="btn-icon"
                          v-b-tooltip.hover
                          title="Voir les détails du bon de retour"
                          :to="{ name: 'bon-retour-show', params: { id: data.item.id } }"
                        >
                          <feather-icon icon="EyeIcon" size="15" />
                        </b-button>
                        <!-- Supplier-only validate action -->
                        <b-button
                          v-if="user.role === 'supplier' && !data.item.is_validated"
                          variant="success"
                          size="sm"
                          class="btn-icon ml-25"
                          v-b-tooltip.hover
                          title="Valider automatiquement ce bon de retour"
                          :disabled="data.item._isValidating"
                          @click="confirmValidateReturnVoucher(data.item)"
                        >
                          <feather-icon :icon="data.item._isValidating ? 'LoaderIcon' : 'CheckCircleIcon'" size="15" />
                        </b-button>
                      </template>
                    </b-table>

                    <!-- Pagination Bar -->
                    <div class="d-flex justify-content-between align-items-center flex-wrap p-2 return-vouchers-pagination">
                      <div class="d-flex align-items-center">
                        <small class="text-muted mr-2">Afficher</small>
                        <b-form-select
                          v-model="returnVouchersPerPage"
                          size="sm"
                          :options="[10, 25, 50, 100]"
                          style="width: 75px;"
                          @change="fetchSupplierReturnVouchers"
                        />
                        <small class="text-muted ml-2">bons sur <strong>{{ returnVouchersTotalCount }}</strong></small>
                      </div>
                      <b-pagination
                        v-model="returnVouchersPage"
                        :total-rows="returnVouchersTotalCount"
                        :per-page="returnVouchersPerPage"
                        size="sm"
                        class="mb-0"
                        @change="onReturnVouchersPageChange"
                      />
                    </div>
                  </b-overlay>

                  <!-- Empty state -->
                  <div
                    v-if="!isReturnVouchersLoading && returnVouchersTotalCount === 0"
                    class="text-center py-3"
                  >
                    <feather-icon icon="RotateCcwIcon" size="32" class="text-muted mb-1" />
                    <p class="text-muted mb-0">Aucun bon de retour trouvé pour ce fournisseur.</p>
                  </div>
                </div>
              </div>
            </b-tab>

            <!-- Invoice Generation Days Tab -->
            <b-tab
              v-if="user && ['admin', 'superadmin', 'sales'].includes(user.role)"
              title="Planification de génération de facture"
            >
              <invoice-generation-days
                :supplier-id="supplier.id"
                @saved="onDaysUpdated"
              />
            </b-tab>
          </b-tabs>
        </b-card>

        <!-- Modal for Changing Supplier Password -->
        <b-modal
          id="supplier-password-modal"
          v-model="showPasswordModal"
          centered
          hide-footer
          size="lg"
          title-tag="div"
          @hidden="resetPasswordForm"
        >
          <template #modal-header="{ close }">
            <div class="w-100 d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="avatar bg-light-primary p-2 mr-2">
                  <feather-icon
                    icon="LockIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <div>
                  <h5 class="mb-0">
                    Changer le mot de passe
                  </h5>
                  <small
                    v-if="supplier"
                    class="text-muted"
                  >
                    {{ supplier.username }} • {{ supplier.email }}
                  </small>
                </div>
              </div>
              <b-button
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                @click="close"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
          </template>

          <div v-if="supplier">
            <!-- Password Audit Log Banner (only shown if a modification exists) -->
            <b-alert
              v-if="supplier.last_password_change"
              show
              variant="light-primary"
              class="mb-3 border-left-primary shadow-sm"
            >
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="ClockIcon"
                  size="20"
                  class="mr-2 text-primary"
                />
                <div>
                  <strong class="d-block text-dark font-weight-bold">
                    Dernière modification du mot de passe
                  </strong>
                  <span class="small text-muted">
                    Modifié le {{ formatDateTime(supplier.last_password_change.created_at) }} par <strong>{{ supplier.last_password_change.changed_by_display }}</strong>
                  </span>
                </div>
              </div>
            </b-alert>

            <!-- Collapsible Audit History -->
            <div
              v-if="supplier.password_change_history && supplier.password_change_history.length > 0"
              class="mb-3"
            >
              <b-button
                v-b-toggle.password-history-collapse
                variant="flat-primary"
                size="sm"
                class="p-0 font-weight-bold"
              >
                <feather-icon
                  icon="HistoryIcon"
                  size="14"
                  class="mr-25"
                />
                Historique des modifications ({{ supplier.password_change_history.length }})
              </b-button>

              <b-collapse
                id="password-history-collapse"
                class="mt-2"
              >
                <b-list-group
                  flush
                  class="border rounded"
                >
                  <b-list-group-item
                    v-for="log in supplier.password_change_history"
                    :key="log.id"
                    class="py-50 px-1 d-flex justify-content-between align-items-center small"
                  >
                    <span>
                      <feather-icon
                        icon="UserCheckIcon"
                        size="12"
                        class="mr-50 text-primary"
                      />
                      <strong>{{ log.changed_by_display }}</strong>
                    </span>
                    <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
                  </b-list-group-item>
                </b-list-group>
              </b-collapse>
            </div>

            <b-form @submit.prevent="savePasswordChange">
              <!-- New Password -->
              <b-form-group label-class="form-label-premium"
                label="Nouveau mot de passe *"
                label-for="modal-password"
                class="mb-3"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input class="premium-input"
                    id="modal-password"
                    v-model="passwordForm.password"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Entrez le nouveau mot de passe"
                    required
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                      class="cursor-pointer"
                      @click="showPassword = !showPassword"
                    />
                  </b-input-group-append>
                </b-input-group>

                <!-- Password Strength Meter -->
                <div
                  v-if="passwordForm.password"
                  class="mt-2"
                >
                  <div class="d-flex justify-content-between align-items-center mb-50">
                    <small class="text-muted">Force du mot de passe:</small>
                    <small
                      :class="'text-' + passwordStrengthClass"
                      class="font-weight-bold"
                    >
                      {{ passwordStrengthText }}
                    </small>
                  </div>
                  <b-progress
                    :value="passwordStrength"
                    :variant="passwordStrengthClass"
                    height="6px"
                  />
                </div>
              </b-form-group>

              <!-- Confirm Password -->
              <b-form-group label-class="form-label-premium"
                label="Confirmer le mot de passe *"
                label-for="modal-confirm-password"
                class="mb-3"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input class="premium-input"
                    id="modal-confirm-password"
                    v-model="passwordForm.confirm_password"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    placeholder="Confirmez le mot de passe"
                    required
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                      class="cursor-pointer"
                      @click="showConfirmPassword = !showConfirmPassword"
                    />
                  </b-input-group-append>
                </b-input-group>

                <small
                  v-if="passwordForm.confirm_password && !passwordsMatch"
                  class="text-danger mt-50 d-block"
                >
                  Les mots de passe ne correspondent pas.
                </small>
              </b-form-group>

              <!-- Generate Password Button -->
              <div class="mb-4">
                <PasswordGenerator
                  :user="supplier"
                  user-type="supplier"
                  button-text="Générer un mot de passe sécurisé"
                  @generated="onPasswordGenerated"
                />
              </div>

              <!-- Action Footer -->
              <div class="d-flex justify-content-end">
                <b-button
                  variant="outline-secondary"
                  class="mr-2"
                  @click="showPasswordModal = false"
                >
                  Annuler
                </b-button>
                <b-button
                  variant="primary"
                  :disabled="isPasswordSubmitting || !isPasswordFormValid"
                  type="submit"
                >
                  <b-spinner
                    v-if="isPasswordSubmitting"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    size="16"
                    class="mr-50"
                  />
                  Enregistrer le mot de passe
                </b-button>
              </div>
            </b-form>
          </div>
        </b-modal>
      </div>
    </b-overlay>
  </div>
</template>

<script>
// Make sure ToastificationContent is properly imported
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import storeAuth from '@/store/store'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BOverlay,
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BListGroup,
  BListGroupItem,
  BTabs,
  BTab,
  BTable,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BProgress,
  BProgressBar,
  BPagination,
  BSpinner,
  BCollapse,
  BFormRadioGroup,
  BFormRadio,
  BFormDatepicker,
  VBToggle,
} from 'bootstrap-vue'
import InvoiceGenerationDays from './components/InvoiceGenerationDays.vue'

export default {
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    PasswordGenerator,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BOverlay,
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BListGroup,
    BListGroupItem,
    BTabs,
    BTab,
    BTable,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BProgress,
    BProgressBar,
    BPagination,
    BSpinner,
    BCollapse,
    BFormRadioGroup,
    BFormRadio,
    BFormDatepicker,
    InvoiceGenerationDays,
  },

  data() {
    return {
      isLoading: false,
      isKpiLoading: false,
      // Internal flag used to suppress the re-fetch that the '$route.query'
      // watcher would otherwise trigger on our own router.replace() write-backs.
      isSyncing: false,
      supplier: null,
      error: null,
      user: null,
      activeTab: 0,
      // Stable string keys per tab — both Planification and Factures are
      // v-if-gated by the same role set, so they render (or not) together and
      // their numeric indices stay consistent. Use a stable key (not index)
      // for URL persistence. 'returnVouchers' sits before 'invoice' so the
      // Planification tab stays last for everyone.
      tabKeys: ['info', 'stats', 'orders', 'invoices', 'returnVouchers', 'invoice'], // index → key
      tabKeyIndex: {
        info: 0, stats: 1, orders: 2, invoices: 3, returnVouchers: 4, invoice: 5,
      }, // key → index
      showPasswordModal: false,
      isPasswordSubmitting: false,
      showPassword: false,
      showConfirmPassword: false,
      kpiPeriod: 'all',
      kpiCustomStartDate: '',
      kpiCustomEndDate: '',
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      dayNamesMap: {
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
      },

      // Embedded Orders Data State
      supplierOrders: [],
      isOrdersLoading: false,
      ordersTotalCount: 0,
      ordersPage: 1,
      ordersPerPage: 10,
      ordersSearch: '',
      ordersStatus: null,
      ordersStartDate: '',
      ordersEndDate: '',
      orderStatusOptions: [
        { value: null, text: 'Tous les statuts' },
        { value: 0, text: 'En attente' },
        { value: 1, text: 'En pickup' },
        { value: 2, text: 'Colis au dépôt' },
        { value: 3, text: 'En cours' },
        { value: 4, text: 'Livré' },
        { value: 5, text: 'Retour au dépôt' },
        { value: 6, text: 'Livré payé' },
        { value: 7, text: 'Retour expéditeur' },
        { value: 8, text: 'En transfert' },
        { value: 9, text: 'Retour payé' },
        { value: 10, text: 'Retour inter-agence' },
        { value: 11, text: 'Retour inter-agence reçu' },
        { value: 12, text: 'Annulé' },
        { value: 13, text: 'Colis perdu' },
        { value: 14, text: 'À vérifier' },
        { value: 15, text: 'En attente de collecte' },
        { value: 16, text: 'À rembourser' },
        { value: 17, text: 'Remboursé' },
        { value: 18, text: 'Planification' },
        { value: 19, text: 'Livré temporaire' },
        { value: 20, text: 'Retour temporaire' },
        { value: 21, text: 'Enlevé' },
        { value: 22, text: 'Retour définitif' },
        { value: 23, text: 'Suivi' },
        { value: 24, text: 'Anomalie de livraison' },
        { value: 25, text: 'Échange' },
        { value: 26, text: 'Échange reçu' },
        { value: 27, text: 'Anomalie pickup' },
        { value: 28, text: 'Retour payé reçu' },
      ],
      orderTableFields: [
        { key: 'tracking_number', label: 'BL' },
        { key: 'client', label: 'Destinataire' },
        { key: 'destination', label: 'Destination' },
        { key: 'status', label: 'Statut' },
        { key: 'price_ttc', label: 'Montant (TND)' },
        { key: 'created_at', label: 'Créée le' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],

      // Embedded Invoices (Factures) Data State — mirrors the Orders block but
      // uses /api/invoices/ with the iStatus/iStart/iEnd/iPage/iSize URL params
      // (the `i` prefix avoids collisions with the orders params above).
      supplierInvoices: [],
      isInvoicesLoading: false,
      invoicesTotalCount: 0,
      invoicesPage: 1,
      invoicesPerPage: 10,
      invoicesStatus: null,
      invoicesStartDate: '',
      invoicesEndDate: '',
      invoiceStatusOptions: [
        { value: null, text: 'Tous les statuts' },
        { value: 'draft', text: 'Brouillon' },
        { value: 'confirmed', text: 'Confirmée' },
        { value: 'cancelled', text: 'Annulée' },
      ],
      invoiceTableFields: [
        { key: 'id', label: 'N° Facture' },
        { key: 'status', label: 'Statut' },
        { key: 'nb_delivered', label: 'Livrés' },
        { key: 'nb_return', label: 'Retours' },
        { key: 'total_ttc', label: 'Total TTC' },
        { key: 'net_a_payer', label: 'Net à payer' },
        { key: 'is_payment_validated', label: 'Paiement' },
        { key: 'confirmed_at', label: 'Confirmée le' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],

      // Embedded Bons de Retour Data State — mirrors the Factures block but
      // uses /api/return-vouchers/ with the rStatus/rStart/rEnd/rPage/rSize URL
      // params (the `r` prefix avoids collisions with the orders and invoices
      // params). Reuses the supplier-scoped list endpoint + the existing
      // supplier "Valider automatiquement" action (validate-return-voucher-orders).
      supplierReturnVouchers: [],
      isReturnVouchersLoading: false,
      returnVouchersTotalCount: 0,
      returnVouchersPage: 1,
      returnVouchersPerPage: 10,
      returnVouchersStatus: null, // null = all, true = validated, false = pending
      returnVouchersStartDate: '',
      returnVouchersEndDate: '',
      returnVoucherStatusOptions: [
        { value: null, text: 'Tous les statuts' },
        { value: true, text: 'Validé' },
        { value: false, text: 'En attente' },
      ],
      returnVoucherTableFields: [
        { key: 'id', label: 'N° Bon' },
        { key: 'orders_count', label: 'Colis' },
        { key: 'total_price', label: 'Prix total' },
        { key: 'total_return_price', label: 'Prix retour' },
        { key: 'is_validated', label: 'Statut' },
        { key: 'created_at', label: 'Créé le' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },

  computed: {
    formattedPaymentDaysHeader() {
      if (!this.supplier || !this.supplier.invoice_generation_days || this.supplier.invoice_generation_days.length === 0) {
        return 'Non configuré'
      }
      return this.supplier.invoice_generation_days
        .map(day => this.formatDayName(day))
        .join(', ')
    },

    passwordStrength() {
      const { password } = this.passwordForm
      if (!password) return 0
      let strength = 0
      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25
      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
      )
    },

    isPasswordFormValid() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordsMatch
        && this.passwordForm.password.length >= 8
      )
    },
  },

  watch: {
    // Re-apply URL → state on browser back/forward so the matched tab
    // and filters are restored. We DON'T call syncQuery() from here (that
    // would create a write-back loop) — the URL already reflects the new
    // state, we just mirror it into component data and fetch.
    '$route.query': function routeQueryWatcher(to, from) {
      // Tab
      if (to.tab !== from.tab) {
        // Both 'invoices' (Factures) and 'invoice' (Planification) require
        // admin/superadmin/sales.
        const billingTabAllowed = !!(this.user && ['admin', 'superadmin', 'sales'].includes(this.user.role))
        const isBillingTab = to.tab === 'invoice' || to.tab === 'invoices'
        if (!to.tab) {
          this.activeTab = 0
        } else if (!isBillingTab || billingTabAllowed) {
          const idx = this.tabKeyIndex[to.tab]
          this.activeTab = idx === undefined ? 0 : idx
        } else {
          // Billing tab requested by a user who can't see it → fall back to info.
          this.activeTab = 0
        }
      }
      // Period + date pickers
      if (to.period !== from.period) {
        if (!to.period || ['all', 'today', '7days', 'this_month', 'custom'].includes(to.period)) {
          this.kpiPeriod = to.period || 'all'
        } else {
          this.kpiPeriod = 'all'
        }
      }
      if (to.kpiStart !== from.kpiStart) this.kpiCustomStartDate = to.kpiStart || ''
      if (to.kpiEnd !== from.kpiEnd) this.kpiCustomEndDate = to.kpiEnd || ''
      // Orders filters
      if (to.q !== from.q) this.ordersSearch = to.q || ''
      if (to.status !== from.status) {
        if (to.status === undefined || to.status === '' || to.status === null) {
          this.ordersStatus = null
        } else {
          const parsed = parseInt(to.status, 10)
          this.ordersStatus = Number.isNaN(parsed) ? null : parsed
        }
      }
      if (to.start !== from.start) this.ordersStartDate = to.start || ''
      if (to.end !== from.end) this.ordersEndDate = to.end || ''
      if (to.size !== from.size) {
        const sz = parseInt(to.size, 10)
        this.ordersPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (to.page !== from.page) {
        const pg = parseInt(to.page, 10)
        this.ordersPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }

      // Invoices (Factures) filters — i-prefixed to avoid collisions with
      // the orders params above (status/start/end/page/size).
      if (to.iStatus !== from.iStatus) {
        if (to.iStatus === undefined || to.iStatus === '' || to.iStatus === null) {
          this.invoicesStatus = null
        } else {
          const valid = ['draft', 'confirmed', 'cancelled'].includes(to.iStatus)
          this.invoicesStatus = valid ? to.iStatus : null
        }
      }
      if (to.iStart !== from.iStart) this.invoicesStartDate = to.iStart || ''
      if (to.iEnd !== from.iEnd) this.invoicesEndDate = to.iEnd || ''
      if (to.iSize !== from.iSize) {
        const sz = parseInt(to.iSize, 10)
        this.invoicesPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (to.iPage !== from.iPage) {
        const pg = parseInt(to.iPage, 10)
        this.invoicesPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }

      // Bons de retour filters — r-prefixed to avoid collisions with the
      // orders and invoices params above. rStatus is a boolean: 'true'/'false'
      // (string) → true/false, else null.
      if (to.rStatus !== from.rStatus) {
        if (to.rStatus === undefined || to.rStatus === '' || to.rStatus === null) {
          this.returnVouchersStatus = null
        } else {
          this.returnVouchersStatus = to.rStatus === 'true'
        }
      }
      if (to.rStart !== from.rStart) this.returnVouchersStartDate = to.rStart || ''
      if (to.rEnd !== from.rEnd) this.returnVouchersEndDate = to.rEnd || ''
      if (to.rSize !== from.rSize) {
        const sz = parseInt(to.rSize, 10)
        this.returnVouchersPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (to.rPage !== from.rPage) {
        const pg = parseInt(to.rPage, 10)
        this.returnVouchersPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }

      // If we ended up on a tab that needs a fetch, trigger it — but ONLY for
      // genuine back/forward navigation. When the URL change was caused by our
      // own syncQuery() write-back (from onTabChange / filter handlers), the
      // fetch has already been issued by the originating handler and we'd
      // double-fetch if we did it again here.
      if (this.isSyncing) return
      const key = this.tabKeys[this.activeTab]
      if (to.tab !== from.tab) {
        if (key === 'stats') this.fetchKpis()
        else if (key === 'orders') this.fetchSupplierOrders()
        else if (key === 'invoices') this.fetchSupplierInvoices()
        else if (key === 'returnVouchers') this.fetchSupplierReturnVouchers()
      }
    },
  },

  async created() {
    this.user = storeAuth.state.user
    // Hydrate component state from the URL query (refresh / shared-link
    // restoration) before the first fetch happens. Anything absent reverts to
    // its default declared above.
    this.applyQueryToState(this.$route.query)
    // Resolve the persisted tab; fall back to 'info' when the user is not
    // allowed to see the role-gated billing tabs (Planification 'invoice' or
    // Factures 'invoices').
    const requestedTab = this.$route.query.tab
    const billingTabAllowed = !!(this.user && ['admin', 'superadmin', 'sales'].includes(this.user.role))
    const isBillingTabKey = requestedTab === 'invoice' || requestedTab === 'invoices'
    if (requestedTab && (!isBillingTabKey || billingTabAllowed)) {
      const idx = this.tabKeyIndex[requestedTab]
      this.activeTab = idx === undefined ? 0 : idx
    } else {
      this.activeTab = 0
    }
    // fetchSupplierDetails() already calls fetchKpis() (lifetime values). For
    // the persisted tab we additionally need:
    //   - 'stats'           → fetchKpis() with the persisted period (the
    //                         lifetime call inside fetchSupplierDetails
    //                         ignores period; re-fetch so the dashboard
    //                         reflects ?period=…).
    //   - 'orders'          → fetchSupplierOrders() (only loads once supplier
    //                         resolves).
    //   - 'invoices'        → fetchSupplierInvoices() (Factures tab; same
    //                         supplier guard).
    //   - 'returnVouchers'  → fetchSupplierReturnVouchers() (Bons de retour
    //                         tab; same supplier guard).
    // We MUST await fetchSupplierDetails() so this.supplier is loaded before
    // triggering the orders/invoices/returnVouchers fetch (otherwise their
    // guards exit).
    await this.fetchSupplierDetails()
    const key = this.tabKeys[this.activeTab]
    if (key === 'stats' && this.kpiPeriod !== 'all') {
      // Only re-fetch when the persisted period is non-default; the lifetime
      // fetch inside fetchSupplierDetails already covered the default.
      this.fetchKpis()
    } else if (key === 'orders') {
      this.fetchSupplierOrders()
    } else if (key === 'invoices') {
      this.fetchSupplierInvoices()
    } else if (key === 'returnVouchers') {
      this.fetchSupplierReturnVouchers()
    }
  },

  methods: {
    async fetchSupplierDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/suppliers/${id}/`)
        this.supplier = data
        // Hydrate ALL KPI fields (incl. return_rate, total_orders_count,
        // average_order_value, …) with lifetime values on first load so the
        // Statistiques tab renders correctly without requiring a tab click.
        // The Commandes table loads on-demand via the tab's @click handler,
        // so we do NOT call fetchSupplierOrders() here (avoids a duplicate
        // network request when the user later opens the Commandes tab).
        this.fetchKpis()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching supplier details:', error)
        this.error = 'Erreur lors du chargement des informations du fournisseur'
      } finally {
        this.isLoading = false
      }
    },

    // ---------------------------------------------------------------
    // URL ↔ state reconciliation
    //
    // The browser URL is the source of truth for the active tab and the
    // Statistiques period / Commandes filters, so a refresh (or a shared
    // link) restores the exact view. We write the URL with $router.replace
    // (NOT push) so in-tab mutations don't pollute the browser history.
    // ---------------------------------------------------------------

    // Push current component state into the URL (replace, not push).
    // Only emits a param when its value differs from the default so the
    // default-visit URL stays clean: /suppliers/show/<id>
    syncQuery() {
      const q = {}
      const tabKey = this.tabKeys[this.activeTab] || 'info'

      const setIf = (key, value, defaultVal) => {
        if (value === undefined || value === null || value === '' || value === defaultVal) return
        q[key] = String(value)
      }

      if (tabKey !== 'info') q.tab = tabKey
      setIf('period', this.kpiPeriod, 'all')
      setIf('kpiStart', this.kpiCustomStartDate, '')
      setIf('kpiEnd', this.kpiCustomEndDate, '')
      setIf('q', this.ordersSearch, '')
      setIf('status', this.ordersStatus, null)
      setIf('start', this.ordersStartDate, '')
      setIf('end', this.ordersEndDate, '')
      setIf('size', this.ordersPerPage, 10)
      setIf('page', this.ordersPage, 1)
      // Factures tab — i-prefixed to avoid collisions with orders params.
      setIf('iStatus', this.invoicesStatus, null)
      setIf('iStart', this.invoicesStartDate, '')
      setIf('iEnd', this.invoicesEndDate, '')
      setIf('iSize', this.invoicesPerPage, 10)
      setIf('iPage', this.invoicesPage, 1)

      // Bons de retour tab — r-prefixed to avoid collisions with orders and
      // invoices params. rStatus is boolean-coerced to 'true'/'false'.
      setIf('rStatus', this.returnVouchersStatus, null)
      setIf('rStart', this.returnVouchersStartDate, '')
      setIf('rEnd', this.returnVouchersEndDate, '')
      setIf('rSize', this.returnVouchersPerPage, 10)
      setIf('rPage', this.returnVouchersPage, 1)

      const same = JSON.stringify(q) === JSON.stringify(this.$route.query || {})
      if (!same) {
        // Mark this replace as originated by us so the '$route.query' watcher
        // can skip its re-fetch logic (only meant for genuine back/forward
        // navigation, not our own write-backs). NavigationDuplicated / abort
        // errors are non-fatal here; ignore them.
        this.isSyncing = true
        this.$router
          .replace({ query: q })
          .catch(() => {})
          .finally(() => {
            this.isSyncing = false
          })
      }
    },

    // Pull the URL query back into component state. Called both on created()
    // (refresh/shared-link) and from the '$route.query' watcher (back/forward).
    applyQueryToState(query) {
      const q = query || {}

      if (q.period) {
        // Only accept known period keys; default otherwise.
        if (['all', 'today', '7days', 'this_month', 'custom'].includes(q.period)) {
          this.kpiPeriod = q.period
        } else {
          this.kpiPeriod = 'all'
        }
      }
      this.kpiCustomStartDate = q.kpiStart || ''
      this.kpiCustomEndDate = q.kpiEnd || ''

      this.ordersSearch = q.q || ''
      if (q.status === undefined || q.status === '' || q.status === null) {
        this.ordersStatus = null
      } else {
        const parsed = parseInt(q.status, 10)
        this.ordersStatus = Number.isNaN(parsed) ? null : parsed
      }
      this.ordersStartDate = q.start || ''
      this.ordersEndDate = q.end || ''

      if (q.size !== undefined) {
        const sz = parseInt(q.size, 10)
        this.ordersPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (q.page !== undefined) {
        const pg = parseInt(q.page, 10)
        this.ordersPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }

      // Factures tab — i-prefixed.
      if (q.iStatus === undefined || q.iStatus === '' || q.iStatus === null) {
        this.invoicesStatus = null
      } else {
        const valid = ['draft', 'confirmed', 'cancelled'].includes(q.iStatus)
        this.invoicesStatus = valid ? q.iStatus : null
      }
      this.invoicesStartDate = q.iStart || ''
      this.invoicesEndDate = q.iEnd || ''
      if (q.iSize !== undefined) {
        const sz = parseInt(q.iSize, 10)
        this.invoicesPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (q.iPage !== undefined) {
        const pg = parseInt(q.iPage, 10)
        this.invoicesPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }

      // Bons de retour — r-prefixed. rStatus is a boolean encoded as a string.
      if (q.rStatus === undefined || q.rStatus === '' || q.rStatus === null) {
        this.returnVouchersStatus = null
      } else {
        this.returnVouchersStatus = q.rStatus === 'true'
      }
      this.returnVouchersStartDate = q.rStart || ''
      this.returnVouchersEndDate = q.rEnd || ''
      if (q.rSize !== undefined) {
        const sz = parseInt(q.rSize, 10)
        this.returnVouchersPerPage = [10, 25, 50, 100].includes(sz) ? sz : 10
      }
      if (q.rPage !== undefined) {
        const pg = parseInt(q.rPage, 10)
        this.returnVouchersPage = Number.isNaN(pg) || pg < 1 ? 1 : pg
      }
    },

    // Single source of truth for tab switches: sets activeTab, triggers the
    // matching fetcher (mirrors the removed <b-tab> @click handlers — those
    // don't fire on programmatic v-model switches from back/forward nav), and
    // syncs the URL. Use this everywhere a tab is changed by code.
    onTabChange(idx) {
      this.activeTab = idx
      const key = this.tabKeys[idx]
      if (key === 'stats') this.fetchKpis()
      else if (key === 'orders') this.fetchSupplierOrders()
      else if (key === 'invoices') this.fetchSupplierInvoices()
      else if (key === 'returnVouchers') this.fetchSupplierReturnVouchers()
      this.syncQuery()
    },

    // Wrapper for the cross-link button (previously `@click="activeTab = 3"`).
    goToTab(key) {
      const idx = this.tabKeyIndex[key]
      if (idx !== undefined) this.onTabChange(idx)
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    formatPrice(price) {
      if (price === null || price === undefined) return 'Non spécifié'
      return `${parseFloat(price).toFixed(3)} DT`
    },

    formatPaymentType(paymentType) {
      if (paymentType === 'virement') return 'Virement'
      if (paymentType === 'espece') return 'Espèce'
      return 'Non spécifié'
    },

    async toggleSupplierStatus() {
      const result = await this.$swal({
        title: this.supplier.is_active ? 'Désactiver le fournisseur?' : 'Activer le fournisseur?',
        text: this.supplier.is_active
          ? 'Le fournisseur ne pourra plus se connecter ni être sélectionné pour les commandes.'
          : 'Le fournisseur pourra à nouveau se connecter et être sélectionné pour les commandes.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, confirmer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })

      if (result.value) {
        try {
          this.isLoading = true
          const { data } = await axios.post(`/api/auth/users/toggle-activation/${this.supplier.id}/`)
          this.supplier.is_active = data.is_active

          this.showToast(
            'success',
            'top-right',
            `Le fournisseur a été ${this.supplier.is_active ? 'activé' : 'désactivé'} avec succès`,
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error toggling supplier status:', error)
          this.showToast(
            'danger',
            'top-right',
            'Erreur lors du changement de statut du fournisseur',
          )
        } finally {
          this.isLoading = false
        }
      }
    },

    async confirmSafeDelete() {
      const result = await this.$swal({
        title: 'Supprimer le fournisseur ?',
        text: `Êtes-vous sûr de vouloir supprimer ${this.supplier.username || 'ce fournisseur'} ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (result.value) {
        try {
          this.isLoading = true
          await axios.delete(`/api/auth/users/suppliers/${this.supplier.id}/`)
          this.showToast(
            'success',
            'top-center',
            'Le fournisseur a été supprimé avec succès',
          )
          this.$router.push({ name: 'supplier-list' })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error soft deleting supplier:', error)
          const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message
          this.showToast(
            'danger',
            'top-center',
            errorMsg,
          )
        } finally {
          this.isLoading = false
        }
      }
    },

    formatDayName(day) {
      if (!day) return ''
      const lower = day.toLowerCase()
      return this.dayNamesMap[lower] || day
    },

    onKpiPeriodChange(period) {
      const today = new Date()
      const formatDate = date => date.toISOString().split('T')[0]

      if (period === 'all') {
        this.kpiCustomStartDate = ''
        this.kpiCustomEndDate = ''
      } else if (period === 'today') {
        const todayStr = formatDate(today)
        this.kpiCustomStartDate = todayStr
        this.kpiCustomEndDate = todayStr
      } else if (period === '7days') {
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(today.getDate() - 7)
        this.kpiCustomStartDate = formatDate(sevenDaysAgo)
        this.kpiCustomEndDate = formatDate(today)
      } else if (period === 'this_month') {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
        this.kpiCustomStartDate = formatDate(firstDay)
        this.kpiCustomEndDate = formatDate(today)
      }

      if (period !== 'custom') {
        this.fetchKpis()
      }
      // Mirror the new period (and the dates it computed) into the URL.
      this.syncQuery()
    },

    async fetchKpis() {
      if (!this.supplier) return

      const params = {}
      if (this.kpiCustomStartDate) {
        params.start_date = this.kpiCustomStartDate
      }
      if (this.kpiCustomEndDate) {
        params.end_date = this.kpiCustomEndDate
      }

      this.isKpiLoading = true
      try {
        const { data } = await axios.get(
          `/api/auth/users/suppliers/${this.supplier.id}/`,
          { params },
        )
        this.$set(this.supplier, 'total_orders_count', data.total_orders_count)
        this.$set(this.supplier, 'active_orders_count', data.active_orders_count)
        this.$set(this.supplier, 'delivered_orders_count', data.delivered_orders_count)
        this.$set(this.supplier, 'returned_orders_count', data.returned_orders_count)
        this.$set(this.supplier, 'total_revenue', data.total_revenue)
        this.$set(this.supplier, 'total_delivery_fees', data.total_delivery_fees)
        this.$set(this.supplier, 'average_order_value', data.average_order_value)
        this.$set(this.supplier, 'delivery_success_rate', data.delivery_success_rate)
        this.$set(this.supplier, 'return_rate', data.return_rate)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error updating KPI metrics:', e)
      } finally {
        this.isKpiLoading = false
        // Keep the URL in sync with the custom date pickers (their @input calls
        // fetchKpis) and with the period radio group (which calls fetchKpis via
        // onKpiPeriodChange). Idempotent: no-op if nothing changed.
        this.syncQuery()
      }
    },

    // Embedded Supplier Orders Methods
    async fetchSupplierOrders() {
      if (!this.supplier) return
      this.isOrdersLoading = true
      try {
        const params = {
          supplier: this.supplier.id,
          page: this.ordersPage,
          page_size: this.ordersPerPage,
          ordering: '-created_at',
        }
        if (this.ordersSearch) {
          params.search = this.ordersSearch
        }
        if (this.ordersStatus !== null && this.ordersStatus !== undefined) {
          params.status = this.ordersStatus
        }
        if (this.ordersStartDate) {
          params.created_at_after = this.ordersStartDate
        }
        if (this.ordersEndDate) {
          params.created_at_before = this.ordersEndDate
        }

        const { data } = await axios.get('/api/orders/list/', { params })
        this.supplierOrders = data.results || data
        this.ordersTotalCount = data.count || (Array.isArray(data) ? data.length : 0)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching supplier orders:', error)
      } finally {
        this.isOrdersLoading = false
        // Mirror current filters/page into the URL (covers status, date pickers,
        // per-page, the search input on Enter, and the reset/page handlers that
        // all funnel through this method). Idempotent when nothing changed.
        this.syncQuery()
      }
    },

    onOrdersPageChange(page) {
      this.ordersPage = page
      this.syncQuery()
      this.fetchSupplierOrders()
    },

    resetOrdersFilters() {
      this.ordersSearch = ''
      this.ordersStatus = null
      this.ordersStartDate = ''
      this.ordersEndDate = ''
      this.ordersPage = 1
      this.syncQuery()
      this.fetchSupplierOrders()
    },

    // ---------------------------------------------------------------
    // Factures (Invoices) tab data methods.
    //
    // Endpoint: GET /api/invoices/ with `supplier=<id>` plus pagination
    // and optional status / created_at__gte / created_at__lte filters.
    // The backend enforces role scoping so users outside the billing team
    // can never reach invoices via this view.
    // ---------------------------------------------------------------
    async fetchSupplierInvoices() {
      if (!this.supplier) return
      this.isInvoicesLoading = true
      try {
        const params = {
          supplier: this.supplier.id,
          page: this.invoicesPage,
          page_size: this.invoicesPerPage,
          ordering: '-created_at',
        }
        if (this.invoicesStatus) {
          params.status = this.invoicesStatus
        }
        if (this.invoicesStartDate) {
          params.created_at__gte = this.invoicesStartDate
        }
        if (this.invoicesEndDate) {
          params.created_at__lte = this.invoicesEndDate
        }

        const { data } = await axios.get('/api/invoices/', { params })
        this.supplierInvoices = data.results || data
        this.invoicesTotalCount = data.count || (Array.isArray(data) ? data.length : 0)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching supplier invoices:', error)
        this.supplierInvoices = []
        this.invoicesTotalCount = 0
      } finally {
        this.isInvoicesLoading = false
        // Mirror current filters/page into the URL (covers status, date pickers,
        // per-page and the reset/page handlers that funnel through this method).
        // Idempotent when nothing changed.
        this.syncQuery()
      }
    },

    onInvoicesPageChange(page) {
      this.invoicesPage = page
      this.syncQuery()
      this.fetchSupplierInvoices()
    },

    resetInvoicesFilters() {
      this.invoicesStatus = null
      this.invoicesStartDate = ''
      this.invoicesEndDate = ''
      this.invoicesPage = 1
      this.syncQuery()
      this.fetchSupplierInvoices()
    },

    // ---------------------------------------------------------------
    // Bons de retour tab data methods.
    //
    // Endpoint: GET /api/return-vouchers/ with `supplier=<id>` plus
    // pagination (`p`, `page_size`) and optional `is_validated` /
    // `created_at__gte` / `created_at__lte` filters. Mirrors the
    // fetchSupplierInvoices plumbing. The backend (ListReturnVoucherAPIView)
    // enforces role-scoped access: suppliers see only their own vouchers via
    // _is_supplier_user() — passing `?supplier=<id>` from a supplier's tab is
    // safe and cannot widen access to another supplier's vouchers.
    // ---------------------------------------------------------------
    async fetchSupplierReturnVouchers() {
      if (!this.supplier) return
      this.isReturnVouchersLoading = true
      try {
        const params = {
          supplier: this.supplier.id,
          p: this.returnVouchersPage,
          page_size: this.returnVouchersPerPage,
          ordering: '-created_at',
        }
        if (this.returnVouchersStatus !== null) {
          params.is_validated = this.returnVouchersStatus
        }
        if (this.returnVouchersStartDate) {
          params.created_at__gte = this.returnVouchersStartDate
        }
        if (this.returnVouchersEndDate) {
          params.created_at__lte = this.returnVouchersEndDate
        }

        const { data } = await axios.get('/api/return-vouchers/', { params })
        this.supplierReturnVouchers = data.results || []
        // In _isValidating flag so reactivity works in the template; default false.
        this.supplierReturnVouchers = this.supplierReturnVouchers.map(v => ({ ...v, _isValidating: false }))
        this.returnVouchersTotalCount = data.count || 0
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching supplier return vouchers:', error)
        this.supplierReturnVouchers = []
        this.returnVouchersTotalCount = 0
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des bons de retour.')
      } finally {
        this.isReturnVouchersLoading = false
        this.syncQuery()
      }
    },

    onReturnVouchersPageChange(page) {
      this.returnVouchersPage = page
      this.syncQuery()
      this.fetchSupplierReturnVouchers()
    },

    resetReturnVouchersFilters() {
      this.returnVouchersStatus = null
      this.returnVouchersStartDate = ''
      this.returnVouchersEndDate = ''
      this.returnVouchersPage = 1
      this.syncQuery()
      this.fetchSupplierReturnVouchers()
    },

    // Confirm-then-validate flow, mirroring returnVouchers/Index.vue so the
    // supplier behavior is identical to the global list (same wording, same
    // endpoint PATCH /api/return-vouchers/validate-return-voucher-orders/).
    // Optimistic UI via the _isValidating row flag, then in-place flip of
    // is_validated + validated_at on success.
    async confirmValidateReturnVoucher(voucher) {
      try {
        const confirmed = await this.$swal({
          title: 'Valider ce bon de retour ?',
          text: `Le bon #${voucher.id} sera validé automatiquement (${voucher.orders_count} colis concerné(s)). Cette action marquera les colis comme « Retour payé reçu ».`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, valider',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
          reverseButtons: true,
          centered: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
          modalClass: 'validation-confirmation-modal',
        })

        if (confirmed) {
          await this.validateReturnVoucher(voucher)
        }
      } catch {
        // User cancelled or closed dialog — do nothing.
      }
    },

    async validateReturnVoucher(voucher) {
      // Optimistic UI: set loading state on the row.
      this.$set(voucher, '_isValidating', true)
      try {
        const { data } = await axios.patch('/api/return-vouchers/validate-return-voucher-orders/', {
          voucher_id: voucher.id,
        })
        if (data && data.success) {
          const idx = this.supplierReturnVouchers.findIndex(v => v.id === voucher.id)
          if (idx !== -1) {
            // Use Vue.set to update properties reactively.
            this.$set(this.supplierReturnVouchers, idx, {
              ...this.supplierReturnVouchers[idx],
              is_validated: true,
              validated_at: data.validated_at,
              _isValidating: false,
            })
          }
          this.showToast('success', 'top-right', data.message || 'Bon de retour validé avec succès.')
        } else {
          this.showToast('danger', 'top-right', (data && data.message) || 'Erreur lors de la validation.')
        }
      } catch (error) {
        let msg = 'Erreur lors de la validation.'
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        }
        this.showToast('danger', 'top-right', msg)
      } finally {
        // Always clear loading state.
        const idx = this.supplierReturnVouchers.findIndex(v => v.id === voucher.id)
        if (idx !== -1) {
          this.$set(this.supplierReturnVouchers[idx], '_isValidating', false)
        }
      }
    },

    // Status helpers mirroring invoices/Index.vue.
    formatInvoiceStatus(status) {
      if (!status) return 'Inconnu'
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },

    getInvoiceStatusVariant(status) {
      switch (status) {
        case 'confirmed':
          return 'success'
        case 'cancelled':
          return 'danger'
        case 'draft':
        default:
          return 'secondary'
      }
    },

    getOrderStatusText(statusCode) {
      const code = parseInt(statusCode, 10)
      const statusMap = {
        0: 'En attente',
        1: 'En pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour expéditeur',
        8: 'En transfert',
        9: 'Retour payé',
        10: 'Retour inter-agence',
        11: 'Retour inter-agence reçu',
        12: 'Annulé',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'À rembourser',
        17: 'Remboursé',
        18: 'Planification',
        19: 'Livré temporaire',
        20: 'Retour temporaire',
        21: 'Enlevé',
        22: 'Retour définitif',
        23: 'Suivi',
        24: 'Anomalie de livraison',
        25: 'Échange',
        26: 'Échange reçu',
        27: 'Anomalie pickup',
        28: 'Retour payé reçu',
      }
      return statusMap[code] || (typeof statusCode === 'string' ? statusCode : `Statut ${statusCode}`)
    },

    getOrderStatusBadgeVariant(statusCode) {
      const code = parseInt(statusCode, 10)
      switch (code) {
        case 0: return 'light-warning'
        case 1: return 'light-info'
        case 2: return 'light-secondary'
        case 3: return 'light-primary'
        case 4: return 'light-success'
        case 5: return 'light-warning'
        case 6: return 'success'
        case 7: return 'light-danger'
        case 8: return 'light-info'
        case 9: return 'danger'
        case 10: return 'light-warning'
        case 11: return 'light-secondary'
        case 12: return 'secondary'
        case 13: return 'danger'
        case 14: return 'light-warning'
        case 15: return 'light-info'
        case 16: return 'light-primary'
        case 17: return 'success'
        case 18: return 'light-info'
        case 19: return 'light-success'
        case 20: return 'light-warning'
        case 21: return 'light-primary'
        case 22: return 'danger'
        case 23: return 'light-info'
        case 24: return 'light-danger'
        case 25: return 'light-primary'
        case 26: return 'light-info'
        case 27: return 'light-danger'
        case 28: return 'danger'
        default: return 'light-primary'
      }
    },

    formatDestination(item) {
      if (!item) return '—'
      let gov = ''
      if (typeof item.governorate === 'string') {
        gov = item.governorate
      } else if (item.governorate && item.governorate.name) {
        gov = item.governorate.name
      } else if (item.governorate_name) {
        gov = item.governorate_name
      }

      let del = ''
      if (typeof item.delegation === 'string') {
        del = item.delegation
      } else if (item.delegation && item.delegation.name) {
        del = item.delegation.name
      } else if (item.delegation_name) {
        del = item.delegation_name
      }

      if (gov && del && gov !== del) {
        return `${gov} (${del})`
      }
      return gov || del || '—'
    },

    onDaysUpdated(newDays) {
      if (this.supplier) {
        this.$set(this.supplier, 'invoice_generation_days', [...newDays])
      }
    },

    openPasswordModal() {
      this.showPasswordModal = true
    },

    resetPasswordForm() {
      this.passwordForm = {
        password: '',
        confirm_password: '',
      }
      this.showPassword = false
      this.showConfirmPassword = false
      this.isPasswordSubmitting = false
    },

    onPasswordGenerated(generatedPassword) {
      this.passwordForm.password = generatedPassword
      this.passwordForm.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return dateStr
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} à ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },

    async savePasswordChange() {
      if (!this.isPasswordFormValid) return

      this.isPasswordSubmitting = true
      try {
        await axios.post(
          `/api/auth/users/suppliers/change-password/${this.supplier.id}/`,
          this.passwordForm,
        )

        const userDisplayName = this.user
          ? `${this.user.username} (${this.user.role || 'Utilisateur'})`
          : 'Utilisateur connecté'

        const newAuditLog = {
          id: Date.now(),
          changed_by_name: this.user ? this.user.username : 'Moi',
          changed_by_display: userDisplayName,
          created_at: new Date().toISOString(),
        }

        this.$set(this.supplier, 'last_password_change', newAuditLog)
        if (!this.supplier.password_change_history) {
          this.$set(this.supplier, 'password_change_history', [])
        }
        this.supplier.password_change_history.unshift(newAuditLog)

        this.showToast(
          'success',
          'top-right',
          'Mot de passe modifié avec succès',
        )
        this.showPasswordModal = false
        this.resetPasswordForm()
      } catch (error) {
        const errorMsg = error.response?.data?.password?.[0]
          || error.response?.data?.confirm_password?.[0]
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'

        this.showToast(
          'danger',
          'top-right',
          errorMsg,
        )
      } finally {
        this.isPasswordSubmitting = false
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.supplier-actions {
  display: flex;
  align-items: center;
}
.stat-card {
  border-radius: 0.75rem;
}

/* Weight pricing table styling */
.table th {
  border-top: none;
  font-weight: 600;
  color: #6e6b7b;
}

.weight-pricing-table {
  margin-bottom: 0;
}

.price-column {
  font-weight: 600;
}

/* ============================================================
   Statistiques & Commandes tabs — Team Parcel Express
   DESIGN_SYSTEM.md: premium dashboard style, soft shadows,
   12px radius, no harsh borders, brand-blue accent (#114092),
   spacious rem-based padding, uppercase table headers.
   ============================================================ */

.supplier-analytics,
.supplier-orders {
  padding: 0.25rem 0;
}

/* Analytics toolbar — subtle brand-tinted background, no harsh border */
.analytics-toolbar {
  background-color: #f2f7fc;
  border: 1px solid rgba(17, 64, 146, 0.08);
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

/* Analytics KPI cards — soft shadow, 12px radius, no harsh border */
.analytics-card {
  border: 0;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.25s ease, transform 0.25s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .card-header {
    border-bottom: 0;
    border-radius: 0.75rem 0.75rem 0 0;
    background-color: #f2f7fc;
  }

  .card-header.analytics-card__header {
    h6 {
      color: #114092; // brand-blue
    }
  }

  .card-header.analytics-card__header--info h6 {
    color: #1f6feb;
  }
}

/* Net amount pill — glassmorphic, left-border accent (per DS §7 Status Flags) */
.analytics-net--positive {
  background-color: rgba(40, 199, 111, 0.08);
  border-left: 4px solid #28c76f;
}

.analytics-net--negative {
  background-color: rgba(234, 84, 85, 0.08);
  border-left: 4px solid #ea5455;
}

/* Premium table wrapper — per DESIGN_SYSTEM.md §5 */
.premium-table-wrapper {
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);

  /* Table headers — uppercase, bold, muted */
  ::v-deep .table thead th {
    background-color: #f8fafc;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    font-size: 0.75rem;
    border-bottom: 2px solid #e2e8f0;
    border-top: 0;
  }

  /* Rows — remove vertical borders, lift on hover */
  ::v-deep .table tbody tr {
    transition: all 0.2s ease;
    &:hover {
      background-color: #f2f7fc;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    }
    td {
      vertical-align: middle;
    }
  }
}

/* Orders date pickers row — replace inline gap with utility spacing */
.orders-date-group {
  gap: 0;
}

.orders-pagination {
  border-top: 1px solid #e2e8f0;
  margin-top: 0.5rem;
}

.weight-category {
  font-size: 0.95rem;
}

/* ============================================================
   Bons de retour tab — Team Parcel Express
   DESIGN_SYSTEM.md §7 (Status Flags): compact glassmorphic card
   with thick left border. No full-width Bootstrap alert.
   §6 (Buttons): smooth hover transitions, no default flat variants.
   ============================================================ */

.supplier-return-vouchers {
  padding: 0.25rem 0;
}

/* Date pickers row — replace inline gap with utility spacing (mirrors
   the orders/invoices date-group convention). */
.return-vouchers-date-group {
  gap: 0;
}

.return-vouchers-pagination {
  border-top: 1px solid #e2e8f0;
  margin-top: 0.5rem;
}

/* Status flag — compact glassmorphic card with thick left border, per
   DESIGN_SYSTEM.md §7. Two states: validated (success) / pending (muted). */
.return-voucher-status-flag {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.6rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-left-width: 4px;
  border-left-style: solid;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  transition: box-shadow 0.2s ease;

  &.validated {
    background-color: rgba(40, 199, 111, 0.08);
    border-left-color: #28c76f;
    color: #1a8a4c;
  }

  &.pending {
    background-color: rgba(148, 163, 184, 0.10);
    border-left-color: #94a3b8;
    color: #64748b;
  }
}

.cursor-not-allowed,
.not-allowed {
  cursor: not-allowed !important;
  pointer-events: auto !important;
  opacity: 0.65;
}
</style>
